"use client";

import { useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import {
  getCandidateCompany,
  hasDisplayCompany,
  hasUsableCandidateDetails,
  normalizeCandidateCountry,
} from "@/lib/search/rawCandidates";

const CandidateSurface = styled(Box)(() => ({
  width: "100%",
  maxWidth: "1040px",
  margin: "0 auto",
}));

const CandidateCard = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(280px, 1.6fr) minmax(200px, 1fr) 88px",
  gap: theme.spacing(3),
  alignItems: "center",
  padding: theme.spacing(1.6, 0.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: "background 0.16s ease",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  "&:last-of-type": {
    borderBottom: 0,
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1.5),
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gridTemplateRows: "auto auto",
    columnGap: theme.spacing(1.25),
    rowGap: theme.spacing(0.25),
    margin: theme.spacing(0, 0, 1),
    padding: theme.spacing(1.35, 1.25),
    border: "none",
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    backgroundColor: theme.palette.background.paper,
    transition: "background-color 0.16s ease, transform 0.16s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:last-of-type": {
      borderBottom: "none",
    },
    "&:active": {
      backgroundColor: theme.palette.action.selected,
      transform: "translateY(1px)",
    },
  },
}));

const NameText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1rem",
  lineHeight: 1.35,
  color: theme.palette.text.primary,
}));

const MetaText = styled(Typography)(({ theme }) => ({
  fontSize: "0.86rem",
  color: theme.palette.text.secondary,
  lineHeight: 1.35,
}));

const CompanyText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.9rem",
  lineHeight: 1.35,
  color: theme.palette.text.primary,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

function CandidateRow({
  candidate,
  onEnrich,
  isEnriching,
  enriched,
  linkedinViewed,
  onLinkedInOpen,
}) {
  const theme = useTheme();
  const fullName = `${candidate.firstName} ${candidate.lastName}`.trim();
  const pictureUrl = candidate.profilePictureUrl || candidate.photoUrl || "";
  const initials =
    `${(candidate.firstName || "").charAt(0)}${(
      candidate.lastName || ""
    ).charAt(0)}`.toUpperCase() || "?";
  const companyLabel = getCandidateCompany(candidate);
  const countryLabel = getCandidateCountry(candidate);
  const roleLabel =
    candidate?.currentPositionTitle || candidate?.title || "Role not listed";
  const handleOpen = () => {
    if (!isEnriching) onEnrich(candidate, { newTab: true });
  };

  return (
    <CandidateCard
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleOpen();
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minWidth: 0,
          pl: { xs: 0, md: 0.5 },
          gridColumn: { xs: "1", sm: "1", md: "1" },
          gridRow: { xs: "1", sm: "auto", md: "auto" },
        }}
      >
        <Avatar
          src={pictureUrl || undefined}
          alt={fullName}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            filter: candidate.enriched === false ? "none" : "grayscale(1)",
            fontSize: "0.95rem",
            fontWeight: 600,
            bgcolor: theme.palette.action.hover,
            color: theme.palette.text.secondary,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <NameText
            sx={
              enriched || linkedinViewed
                ? { color: theme.palette.primary.main }
                : {}
            }
          >
            {fullName || "Unknown"}
          </NameText>
          <MetaText sx={{ mt: 0.35 }}>{roleLabel}</MetaText>
        </Box>
      </Box>

      <Box
        sx={{
          minWidth: 0,
          display: { xs: "flex", sm: "block" },
          alignItems: "center",
          gap: 0.7,
          gridColumn: { xs: "1", sm: "auto", md: "2" },
          gridRow: { xs: "2", sm: "auto", md: "auto" },
          // Stacked layouts: indent past the avatar so this line starts where
          // the name and role text start.
          pl: { xs: "50px", md: 0 },
        }}
      >
        <CompanyText noWrap={Boolean(companyLabel)}>
          {companyLabel || "Company not listed"}
        </CompanyText>
        {countryLabel && (
          <>
            <Box
              component="span"
              sx={{
                display: { xs: "inline", sm: "none" },
                color: "text.disabled",
                fontSize: "0.75rem",
              }}
            >
              ·
            </Box>
            <MetaText
              sx={{
                mt: { xs: 0, sm: 0.35 },
              }}
            >
              {countryLabel}
            </MetaText>
          </>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "flex-end", md: "flex-end" },
          gap: 0.5,
          gridColumn: { xs: "2", sm: "auto", md: "3" },
          gridRow: { xs: "1", sm: "auto", md: "auto" },
        }}
      >
        {candidate.linkedinUrl && (
          <Tooltip title="View LinkedIn profile" arrow>
            <IconButton
              component="a"
              href={candidate.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.stopPropagation();
                onLinkedInOpen(candidate.linkedinUrl);
              }}
              onKeyDown={(event) => event.stopPropagation()}
              sx={{
                width: { xs: 46, md: 38 },
                height: { xs: 46, md: 38 },
                color: "#0A66C2",
                backgroundColor: "transparent",
                opacity: 0.78,
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <LinkedInIcon
                sx={{ fontSize: { xs: "1.55rem", md: "1.4rem" } }}
              />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Open Meex profile" arrow>
          <span>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleOpen();
              }}
              disabled={isEnriching}
              sx={{
                width: { xs: 46, md: 38 },
                height: { xs: 46, md: 38 },
                backgroundColor: "transparent",
                opacity: 0.82,
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              {isEnriching ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Box
                  component="img"
                  src="/images/logos/logoIcon.png"
                  alt=""
                  sx={{
                    width: { xs: 23, md: 21 },
                    height: { xs: 23, md: 21 },
                    display: "block",
                  }}
                />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </CandidateCard>
  );
}

function getCandidateCountry(candidate) {
  const countryValue = String(
    candidate?.country || candidate?.countryCode || "",
  )
    .trim()
    .toLowerCase();
  if (countryValue === "usa" || countryValue === "us") {
    return "United States";
  }

  return normalizeCandidateCountry(candidate?.country, candidate?.countryCode);
}

// Words that follow "of" inside a job title ("Head of Marketing"), so the
// phrase names a function rather than an employer.
const TITLE_FUNCTION_WORDS = /^(marketing|sales|engineering|product|design|operations|ops|finance|hr|people|talent|recruiting|growth|data|analytics|security|legal|communications?|comms|partnerships?|strategy|research|content|revenue|customer\s+success|customer\s+experience|business\s+development|bizdev|it|support|technology|digital|brand|community|programs?|projects?|staff|department|division)\b/i;

// Descriptors that follow a connector but are not a real employer name.
const GENERIC_COMPANY_DESCRIPTOR = /^(a\s+|an\s+|the\s+|early-stage\s+|startups?\b|small\s+|mid-size\s+|medium\s+|large\s+|enterprise\s+|us\s+|usa\s+|united\s+states\s+|american\s+|software\s+companies?\b|saas\s+companies?\b|tech\s+companies?\b|companies?\b|firms?\b)/i;

// Finds the last "at/of/for/from/@ <employer>" clause, skipping title phrases
// like "Head of Marketing" and generic descriptors like "at a startup".
function detectCompanyPhrase(query) {
  const connectorPattern = /\b(at|of|for|from)\s+|@\s*/gi;
  let best = null;
  let match;
  while ((match = connectorPattern.exec(query)) !== null) {
    const connector = (match[1] || "@").toLowerCase();
    const rest = query.slice(match.index + match[0].length).trim();
    if (!rest) continue;
    if (connector === "of" && TITLE_FUNCTION_WORDS.test(rest)) continue;
    if (GENERIC_COMPANY_DESCRIPTOR.test(rest)) continue;
    best = { index: match.index, name: rest.replace(/\s+\bin\s+.+$/i, "") };
  }
  return best;
}

// Where the role stops and trailing qualifiers begin. Covers generic clauses
// like "at large NYC firms" that detectCompanyPhrase deliberately ignores.
function findRoleCoreCutIndex(query, companyPhrase) {
  const qualifier = query.match(/\b(?:at|in|for|from)\s+|@\s*/i);
  return Math.min(
    qualifier ? qualifier.index : Infinity,
    companyPhrase ? companyPhrase.index : Infinity,
  );
}

// Roles that already name an organisation type, so "at Stripe" would contradict
// the role itself ("Ad agency owners at Stripe").
const ORG_TYPE_ROLE = /\b(agency|agencies|firm|firms|studio|studios|practice|practices|brokerage|clinic|clinics)\b/i;

// Sectors rather than places, so "in fintech" is not treated as a location.
const INDUSTRY_PHRASE = /^((?:fin|insur|prop|health|ed|ad|bio|med|clean|deep|agri|reg)?tech|climate tech|saas|software|technology|healthcare|health care|finance|financial services|banking|insurance|retail|ecommerce|e-commerce|manufacturing|logistics|real estate|education|media|advertising|marketing|consulting|legal|law|energy|oil and gas|mining|agriculture|hospitality|travel|gaming|games|crypto|web3|blockchain|ai|artificial intelligence|machine learning|cybersecurity|security|telecom|telecommunications|automotive|aerospace|defense|defence|pharma|pharmaceuticals|nonprofit|non-profit|government|public sector|sports|fashion|beauty|food|beverage|cpg|construction|architecture)$/i;

function buildRoleHintExample(query) {
  const trimmedQuery = (query || "").trim();
  if (!trimmedQuery) {
    return {
      leadText: "Not above? Add a company, like ",
      actionLabel: "“Founders at Stripe”",
      trailingText: "to narrow the list.",
    };
  }

  const lowerQuery = trimmedQuery.toLowerCase();
  const mentionsOpenToWork = /\bopen to work\b|\bopentowork\b|\blooking for (a )?job\b|\bjob seeking\b/i.test(
    trimmedQuery,
  );
  const hasSpecificLocation =
    /\bin\s+[a-z]/i.test(trimmedQuery) ||
    /\b(sf|nyc|london|paris|berlin|toronto|singapore|dubai)\b/i.test(
      trimmedQuery,
    ) ||
    /\b(us|usa|united states|america|uk|united kingdom|canada|europe)\b/i.test(
      trimmedQuery,
    );
  const companyPhrase = detectCompanyPhrase(trimmedQuery);
  const hasSpecificCompany = !!companyPhrase;

  // Only descriptor words that are not also common job-title words: stripping
  // "software" or "firm" would mangle "Software Engineer" / "law firm partners".
  const rawRoleSlice = trimmedQuery.slice(
    0,
    findRoleCoreCutIndex(trimmedQuery, companyPhrase),
  );
  let roleCore = rawRoleSlice
    .replace(
      /\b(us|usa|united states|america|software companies|saas companies|companies|company)\b/gi,
      "",
    )
    .replace(/\b(early-stage|startup|startups|mid-size)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!roleCore) {
    if (/\bfounders?\b|\bco-?founders?\b/i.test(lowerQuery)) {
      roleCore = "Founders";
    } else if (/\bheads?\s+of\b/i.test(trimmedQuery)) {
      roleCore = trimmedQuery.match(/\bheads?\s+of\s+[\p{L}\s]+/iu)?.[0] || "";
    }
  }

  if (mentionsOpenToWork && !/\bopen to work\b/i.test(roleCore)) {
    roleCore = `${roleCore} open to work`.trim();
  }

  // Carry over the place the user already typed so the example never silently
  // drops their location. An industry is dropped instead, because naming a
  // company already implies it ("nurse at Company in healthcare" is redundant).
  const trailingInSubject =
    trimmedQuery.match(/\bin\s+([\p{L}][\p{L}\s.'-]*)$/iu)?.[1]?.trim() || "";
  const locationSuffix =
    trailingInSubject && !INDUSTRY_PHRASE.test(trailingInSubject)
      ? `in ${trailingInSubject}`
      : "";
  const companyExample = ORG_TYPE_ROLE.test(rawRoleSlice)
    ? "Company"
    : "Stripe";
  const withCompany = () =>
    `“${[roleCore || "Founders", `at ${companyExample}`, locationSuffix]
      .filter(Boolean)
      .join(" ")}”`;

  if (hasSpecificCompany && !hasSpecificLocation) {
    return {
      leadText: "Not above? Add a location, like ",
      actionLabel: `“${trimmedQuery} in San Francisco”`,
      trailingText: "to narrow the list.",
    };
  }

  if (hasSpecificCompany) {
    return {
      leadText: "Not above? Try a different company, like ",
      actionLabel: withCompany(),
      trailingText: "to narrow the list.",
    };
  }

  return {
    leadText: "Not above? Add a company, like ",
    actionLabel: withCompany(),
    trailingText: "to narrow the list.",
  };
}

export default function CandidateList({
  candidates = [],
  enrichingId = null,
  enrichedUrls = new Set(),
  onEnrich,
  onRefineSearch,
  query = "",
  showNarrowSearchHint: showNarrowSearchHintProp,
  allowMissingCompany = false,
  narrowSearchHintMode = "name",
}) {
  const theme = useTheme();
  const [viewedLinkedInUrls, setViewedLinkedInUrls] = useState(new Set());
  const displayCandidates = candidates.filter((candidate) =>
    allowMissingCompany
      ? hasUsableCandidateDetails(candidate)
      : hasDisplayCompany(candidate),
  );

  if (displayCandidates.length === 0) return null;

  const showNarrowSearchHint =
    showNarrowSearchHintProp ?? candidates.length >= 100;
  const roleHintCopy = buildRoleHintExample(query);
  const narrowHintLeadText =
    narrowSearchHintMode === "role"
      ? roleHintCopy.leadText
      : "Not above? Search ";
  const narrowHintActionLabel =
    narrowSearchHintMode === "role"
      ? roleHintCopy.actionLabel
      : query
      ? `"${query} at Company"`
      : "with name at company";
  const narrowHintTrailingText =
    narrowSearchHintMode === "role"
      ? roleHintCopy.trailingText
      : "to find the right person.";
  return (
    <CandidateSurface sx={{ mt: 1 }}>
      <Box>
        {displayCandidates.map((candidate, index) => (
          <CandidateRow
            key={`${candidate.linkedinUrl || candidate.firstName}-${
              candidate.lastName
            }-${index}`}
            candidate={candidate}
            onEnrich={onEnrich}
            isEnriching={enrichingId === candidate.linkedinUrl}
            enriched={enrichedUrls.has(candidate.linkedinUrl)}
            linkedinViewed={viewedLinkedInUrls.has(candidate.linkedinUrl)}
            onLinkedInOpen={(linkedinUrl) => {
              setViewedLinkedInUrls((prev) => new Set(prev).add(linkedinUrl));
            }}
          />
        ))}
      </Box>

      {showNarrowSearchHint && (
        <Box
          sx={{
            mt: 2.5,
            px: 2,
            py: 1.5,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, fontSize: "0.86rem" }}
          >
            {narrowHintLeadText}
            <Box
              component="button"
              type="button"
              onClick={onRefineSearch}
              sx={{
                appearance: "none",
                border: 0,
                background: "transparent",
                p: 0,
                color: theme.palette.primary.main,
                cursor: "pointer",
                font: "inherit",
                fontWeight: 560,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {narrowHintActionLabel}
            </Box>{" "}
            {narrowHintTrailingText}
          </Typography>
        </Box>
      )}
    </CandidateSurface>
  );
}
